<template>
  <div :class="['ai-message-bubble', message.role === 'user' ? 'bubble-right' : 'bubble-left']">
    <div v-if="message.role === 'assistant'" class="avatar ai-avatar">
      <svg viewBox="0 0 40 40" width="36" height="36">
        <defs>
          <linearGradient id="aiGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#009798" />
            <stop offset="100%" style="stop-color:#007e7f" />
          </linearGradient>
        </defs>
        <circle cx="20" cy="20" r="20" fill="url(#aiGrad)" />
        <text x="20" y="25" text-anchor="middle" fill="white" font-size="16" font-weight="bold">灵机</text>
      </svg>
    </div>
    <div class="bubble-content">
      <div :class="['bubble-body', isStreaming ? 'streaming' : '', message.isLoading ? 'loading' : '']">
        <template v-if="message.role === 'user'">
          <p v-if="message.messageType === 'text' || !message.imageUrl">{{ message.content }}</p>
          <p v-if="message.messageType === 'image' && message.content">{{ message.content }}</p>
          <div v-if="message.imageUrl" class="bubble-image">
            <img :src="message.imageUrl" @click="previewImage(message.imageUrl)" />
          </div>
        </template>
        <template v-else>
          <div class="ai-text" v-html="renderContent(displayContent)"></div>
          <span v-if="isStreaming" class="typing-cursor"></span>
          
        </template>
      </div>
      <!-- 用户消息操作按钮 -->
      <div class="bubble-actions-bottom" v-if="message.role === 'user'">
        <button class="action-btn-inline" @click="copyMessage" title="复制">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
          <span>复制</span>
        </button>
        <button class="action-btn-inline" @click="resendMessage" title="重发">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 4v6h6"></path>
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
          </svg>
          <span>重发</span>
        </button>
      </div>
      <!-- AI消息操作按钮 -->
      <div class="bubble-actions-bottom" v-if="message.role === 'assistant' && !isStreaming">
        <button class="action-btn-inline" @click="copyMessage" title="复制">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
          <span>复制</span>
        </button>
        <button 
          class="action-btn-inline" 
          :class="{ 'speaking': isSpeaking }"
          @click="toggleSpeak" 
          :title="isSpeaking ? '停止朗读' : '朗读'"
        >
          <svg v-if="!isSpeaking" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
          </svg>
          <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="currentColor" stroke="currentColor" stroke-width="2">
            <rect x="6" y="4" width="4" height="16"></rect>
            <rect x="14" y="4" width="4" height="16"></rect>
          </svg>
          <span>{{ isSpeaking ? '停止' : '朗读' }}</span>
        </button>
      </div>
      <!-- <div v-if="parsedOptions.length > 0" class="ai-options-list">
            <div
              v-for="(opt, optIdx) in parsedOptions"
              :key="optIdx"
              class="ai-option-item"
              @click="handleOptionClick(opt)"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#009798" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>{{ opt }}</span>
            </div>
          </div> -->
      <div class="bubble-time">{{ formatTime(message.createTime) }}</div>
    </div>
    <div v-if="message.role === 'user'" class="avatar user-avatar">
      <svg viewBox="0 0 40 40" width="36" height="36">
        <circle cx="20" cy="20" r="20" fill="#eaf6f4" />
        <circle cx="20" cy="15" r="7" fill="#009798" />
        <path d="M6 36 C6 25 34 25 34 36" fill="#009798" />
      </svg>
    </div>
  </div>
</template>

<script>
import marked from 'marked'
import { markdownToPlainText } from '../../js/markdown.js'

// 配置 marked：开启 GFM（表格、删除线等）和换行转 <br>
marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: false,
  mangle: false
})

export default {
  name: 'AiMessageBubble',
  props: {
    message: { type: Object, required: true },
    isStreaming: { type: Boolean, default: false },
    isSpeaking: { type: Boolean, default: false }
  },
  computed: {
    displayContent() {
      if (this.message.isLoading) {
        return this.animatedDots
      }
      return this.message.content
    },
    parsedOptions() {
      if (!this.message.content) return []
      var content = this.message.content
      var optionsMatch = content.match(/<options>([\s\S]*?)<\/options>/)
      if (!optionsMatch) return []
      var optionsText = optionsMatch[1]
      var results = []
      var regex = /<option>([\s\S]*?)<\/option>/g
      var match
      while ((match = regex.exec(optionsText)) !== null) {
        var text = match[1].trim()
        if (text) results.push(text)
      }
      return results
    },
    animatedDots() {
      // 动态显示不同数量的点
      const dots = ['', '.', '..', '...']
      const index = Math.floor(Date.now() / 500) % 4
      return '灵机正在思考' + dots[index]
    }
  },
  data() {
    return {
      dotsTimer: null
    }
  },
  mounted() {
    if (this.message.isLoading) {
      this.startDotsAnimation()
    }
  },
  beforeDestroy() {
    if (this.dotsTimer) {
      clearInterval(this.dotsTimer)
    }
  },
  watch: {
    'message.isLoading'(newVal) {
      if (newVal) {
        this.startDotsAnimation()
      } else {
        if (this.dotsTimer) {
          clearInterval(this.dotsTimer)
        }
      }
    }
  },
  methods: {
    startDotsAnimation() {
      if (this.dotsTimer) {
        clearInterval(this.dotsTimer)
      }
      this.dotsTimer = setInterval(() => {
        this.$forceUpdate()
      }, 500)
    },
    renderContent(text) {
      if (!text) return ''
      // 先剥离自定义的 <options> 标签
      var cleanText = text.replace(/<options>[\s\S]*?<\/options>/g, '')
      if (this.isStreaming) {
        // 流式输出期间：显示纯文本，做 HTML 转义 + 换行，避免未闭合的 Markdown 标记导致中间态闪烁
        return this.escapeHtml(cleanText).replace(/\n/g, '<br>')
      }
      // 流式结束后及历史消息：使用 marked 完整渲染 Markdown
      try {
        return marked.parse(cleanText)
      } catch (e) {
        console.error('Markdown 渲染失败:', e)
        return this.escapeHtml(cleanText).replace(/\n/g, '<br>')
      }
    },
    escapeHtml(str) {
      if (!str) return ''
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    formatTime(time) {
      if (!time) return ''
      var d = new Date(time)
      var h = d.getHours().toString().padStart(2, '0')
      var m = d.getMinutes().toString().padStart(2, '0')
      return h + ':' + m
    },
    previewImage(url) {
      if (url) {
        window.open(url, '_blank')
      }
    },
    copyMessage() {
      var text = markdownToPlainText(this.message.content || '')
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$emit('copied', '消息已复制到剪贴板')
        }).catch(err => {
          console.error('复制失败:', err)
          this.$emit('copy-error', '复制失败，请重试')
        })
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        const success = document.execCommand('copy')
        document.body.removeChild(textArea)
        if (success) {
          this.$emit('copied', '消息已复制到剪贴板')
        } else {
          this.$emit('copy-error', '复制失败，请重试')
        }
      }
    },
    resendMessage() {
      // 触发重发事件，传递消息内容给父组件
      this.$emit('resend', this.message)
    },
    toggleSpeak() {
      // 触发朗读或停止事件
      this.$emit('speak', this.message)
    },
    handleOptionClick(optText) {
      this.$emit('option-click', optText)
    }
  }
}
</script>

<style scoped>
.ai-message-bubble {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  padding: 0 8px;
}
.bubble-right {
  flex-direction: row;
  justify-content: flex-end;
}
.bubble-left {
  flex-direction: row;
}
.avatar {
  flex-shrink: 0;
  margin: 0 8px;
}
.bubble-content {
  max-width: 75%;
  display: flex;
  flex-direction: column;
}
.bubble-right .bubble-content {
  align-items: flex-end;
}
.bubble-body {
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
  position: relative;
  text-align: left;
}
.bubble-right .bubble-body {
  background: linear-gradient(135deg, #009798, #007e7f);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.bubble-left .bubble-body {
  background: rgba(255, 255, 255, 0.9);
  color: #1F2937;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.ai-text {
  text-align: left;
  line-height: 1.7;
  word-break: break-word;
}
/* Markdown 元素样式 */
.ai-text >>> p {
  margin: 0 0 8px;
}
.ai-text >>> p:last-child {
  margin-bottom: 0;
}
.ai-text >>> h1,
.ai-text >>> h2,
.ai-text >>> h3,
.ai-text >>> h4,
.ai-text >>> h5,
.ai-text >>> h6 {
  margin: 14px 0 8px;
  font-weight: 600;
  line-height: 1.4;
}
.ai-text >>> h1 {
  font-size: 20px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 6px;
}
.ai-text >>> h2 {
  font-size: 17px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 4px;
}
.ai-text >>> h3 {
  font-size: 15px;
}
.ai-text >>> h4 {
  font-size: 14px;
}
.ai-text >>> h5,
.ai-text >>> h6 {
  font-size: 13px;
  color: #6b7280;
}
.ai-text >>> ul,
.ai-text >>> ol {
  margin: 6px 0 8px;
  padding-left: 22px;
}
.ai-text >>> li {
  margin: 3px 0;
}
.ai-text >>> li > ul,
.ai-text >>> li > ol {
  margin: 3px 0;
}
.ai-text >>> strong {
  font-weight: 600;
}
.ai-text >>> em {
  font-style: italic;
}
.ai-text >>> del {
  color: #9ca3af;
}
.ai-text >>> a {
  color: #009798;
  text-decoration: none;
}
.ai-text >>> a:hover {
  text-decoration: underline;
}
/* 行内代码 */
.ai-text >>> code {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 13px;
  font-family: Consolas, 'Courier New', monospace;
  color: #d6336c;
}
/* 代码块 */
.ai-text >>> pre {
  background: #1f2937;
  color: #e5e7eb;
  padding: 12px 14px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 8px 0;
  font-size: 13px;
  line-height: 1.6;
}
.ai-text >>> pre code {
  background: transparent;
  color: inherit;
  padding: 0;
  border-radius: 0;
  font-size: inherit;
  font-family: Consolas, 'Courier New', monospace;
}
/* 引用块 */
.ai-text >>> blockquote {
  margin: 8px 0;
  padding: 6px 12px;
  border-left: 3px solid #009798;
  background: rgba(0, 151, 152, 0.06);
  color: #4b5563;
}
.ai-text >>> blockquote p {
  margin: 0;
}
/* 表格 */
.ai-text >>> table {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
  font-size: 13px;
  display: block;
  overflow-x: auto;
}
.ai-text >>> thead {
  background: #f3f4f6;
}
.ai-text >>> th,
.ai-text >>> td {
  border: 1px solid #e5e7eb;
  padding: 6px 10px;
  text-align: left;
}
.ai-text >>> th {
  font-weight: 600;
}
.ai-text >>> tbody tr:nth-child(even) {
  background: #fafafa;
}
.ai-text >>> hr {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 12px 0;
}
.ai-text >>> img {
  max-width: 100%;
  border-radius: 6px;
}
.bubble-left .bubble-body.loading {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 252, 248, 0.95));
  color: #009798;
  font-style: italic;
}
.bubble-image {
  margin-top: 6px;
}
.bubble-image img {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}
.bubble-image img:hover {
  transform: scale(1.03);
}
.bubble-time {
  font-size: 11px;
  color: #9CA3AF;
  margin-top: 4px;
  padding: 0 4px;
  text-align: left;
}
.typing-cursor {
  display: inline-block;
  width: 3px;
  height: 18px;
  background: linear-gradient(to bottom, #009798, #007e7f);
  margin-left: 3px;
  vertical-align: text-bottom;
  border-radius: 1px;
  animation: blink 0.8s infinite, pulse 2s infinite;
  box-shadow: 0 0 4px rgba(0, 151, 152, 0.4);
}
@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0.3; }
}
@keyframes pulse {
  0% { box-shadow: 0 0 4px rgba(0, 151, 152, 0.4); }
  50% { box-shadow: 0 0 8px rgba(0, 151, 152, 0.6); }
  100% { box-shadow: 0 0 4px rgba(0, 151, 152, 0.4); }
}
/* 流式输出时的消息气泡背景效果 */
.bubble-left .bubble-body.streaming {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 252, 248, 0.95));
  box-shadow: 0 1px 6px rgba(0, 151, 152, 0.1), 0 1px 4px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;
}
.bubble-left .bubble-body.streaming::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0, 151, 152, 0.3), transparent);
  animation: streaming-line 2s infinite;
}
@keyframes streaming-line {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
/* 底部操作按钮样式 */
.bubble-actions-bottom {
  display: flex;
  gap: 8px;
  margin-top: 6px;
  padding: 0 4px;
}
.action-btn-inline {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: #fff;
  color: #6B7280;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.action-btn-inline:hover {
  background: #F3F4F6;
  border-color: #009798;
  color: #009798;
}
.action-btn-inline.speaking {
  background: rgba(0, 151, 152, 0.1);
  border-color: #009798;
  color: #009798;
}
.action-btn-inline svg {
  flex-shrink: 0;
}
.bubble-body p {
  margin: 0;
}
.ai-options-list {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ai-option-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(0, 151, 152, 0.06);
  border: 1px solid rgba(0, 151, 152, 0.2);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
}
.ai-option-item:hover {
  background: rgba(0, 151, 152, 0.12);
  border-color: rgba(0, 151, 152, 0.4);
  color: #1F2937;
}
.ai-option-item svg {
  flex-shrink: 0;
  margin-top: 2px;
}
</style>
